<template>
  <div>
    <div class="m-container py-8">
      <!-- Loading Skeleton -->
      <template v-if="loading">
        <MSkeletonLoader type="text" :width="400" class="mb-4" />
        <div class="m-row">
          <div class="m-col m-col-12 m-col-md-5">
            <MSkeletonLoader type="image" :height="400" />
          </div>
          <div class="m-col m-col-12 m-col-md-7">
            <MSkeletonLoader type="heading" class="mb-4" />
            <MSkeletonLoader type="text" class="mb-2" />
            <MSkeletonLoader type="text" class="mb-2" />
            <MSkeletonLoader type="text" class="mb-4" />
            <MSkeletonLoader type="button" :width="200" />
          </div>
        </div>
      </template>

      <template v-else-if="product">
        <!-- Breadcrumbs -->
        <nav aria-label="Brotkrümel-Navigation">
          <MBreadcrumbs :items="breadcrumbItems" class="mb-2" />
        </nav>

        <!-- Hauptbereich: Bilder links, Infos + Preisvergleich rechts -->
        <div class="m-row mb-8">
          <!-- Bildergalerie LINKS -->
          <div class="m-col m-col-12 m-col-md-5">
            <div class="gallery">
              <div class="gallery__main-wrap">
                <MImg
                  :src="activeImage || '/placeholder.svg'"
                  :alt="product.name"
                  contain
                  priority
                  :proxy-width="900"
                  class="gallery__main"
                  @click="openLightbox(activeImageIndex)"
                />
                <!-- Wishlist & Share oben rechts -->
                <div class="gallery__actions">
                  <ClientOnly>
                    <button class="gallery__action-btn" :aria-label="isWishlisted ? 'Von Merkliste entfernen' : 'Zur Merkliste'" @click="onToggleWishlist">
                      <MIcon :icon="isWishlisted ? 'heart' : 'heart-outline'" :size="22" :color="isWishlisted ? 'var(--m-secondary)' : 'rgba(0,0,0,0.4)'" />
                    </button>
                  </ClientOnly>
                  <button v-if="canShare" class="gallery__action-btn" aria-label="Teilen" @click="onShare">
                    <MIcon icon="share-variant-outline" :size="20" color="rgba(0,0,0,0.4)" />
                  </button>
                </div>
                <!-- Bild-Navigation -->
                <button v-if="allImages.length > 1" class="gallery__nav gallery__nav--prev" aria-label="Vorheriges Bild" @click="prevImage">
                  <MIcon icon="chevron-left" :size="24" />
                </button>
                <button v-if="allImages.length > 1" class="gallery__nav gallery__nav--next" aria-label="Nächstes Bild" @click="nextImage">
                  <MIcon icon="chevron-right" :size="24" />
                </button>
              </div>
              <div v-if="allImages.length > 1" class="d-flex flex-wrap m-gap-2 mt-3">
                <MAvatar
                  v-for="(img, i) in allImages"
                  :key="i"
                  :size="56"
                  rounded
                  class="gallery__thumb"
                  :class="{ 'gallery__thumb--active': img === activeImage }"
                  @click="activeImage = img"
                >
                  <MImg :src="img" :alt="`${product.name} Bild ${i + 1}`" contain :width="56" :height="56" />
                </MAvatar>
              </div>
            </div>
          </div>

          <!-- Produktinfos + Preisvergleich RECHTS -->
          <div class="m-col m-col-12 m-col-md-7">
            <h1 class="pd-title">{{ product.name }}</h1>

            <!-- Kurzinfo wie moebel.de: Alle Details | Farbe: Beige | Maße: ... -->
            <div class="pd-meta">
              <a :href="product.affiliateUrl" target="_blank" rel="noopener sponsored" class="pd-meta__item pd-meta__link" @click="onAffiliateClick">Alle Details</a>
              <span v-if="product.brand" class="pd-meta__item">Marke: <strong>{{ product.brand }}</strong></span>
              <span v-if="activeVariantColourLabel" class="pd-meta__item">Farbe: <strong>{{ activeVariantColourLabel }}</strong></span>
              <span v-if="dimensionsLabel" class="pd-meta__item">Maße: <strong>{{ dimensionsLabel }}</strong></span>
            </div>

            <!-- Varianten -->
            <div v-if="displayVariants.length > 1" class="pd-variants mb-4">
              <div class="pd-variants__label">
                Variante: <strong>{{ activeVariantLabel }}</strong>
              </div>
              <div class="d-flex flex-wrap m-gap-2 mt-2">
                <div
                  v-for="dv in displayVariants"
                  :key="dv.id"
                  class="pd-variant-card"
                  :class="{ 'pd-variant-card--active': dv.id === activeVariantId }"
                  role="radio"
                  :aria-checked="dv.id === activeVariantId"
                  :aria-label="`Variante ${dv.label}`"
                  :tabindex="0"
                  @click="activeVariantId = dv.id"
                  @keydown.enter="activeVariantId = dv.id"
                  @keydown.space.prevent="activeVariantId = dv.id"
                >
                  <MImg :src="dv.image || '/placeholder.svg'" :width="48" :height="48" contain class="pd-variant-card__img" />
                  <div class="pd-variant-card__info">
                    <span class="pd-variant-card__name">{{ dv.label }}</span>
                    <span class="pd-variant-card__price">ab {{ formatPrice(dv.variant?.price || 0) }}</span>
                  </div>
                </div>
              </div>
            </div>

            <!-- Angebote-Header -->
            <div class="pd-offers-header">
              <div class="pd-offers-header__left">
                <span class="pd-offers-count">
                  {{ allOffers.length }} {{ allOffers.length === 1 ? 'Angebot' : 'Angebote' }}
                </span>
                <span class="pd-offers-range">
                  ab {{ formatPrice(showTotalPrice ? cheapestTotalPrice : cheapestPrice) }}
                  <template v-if="allOffers.length > 1"> - {{ formatPrice(showTotalPrice ? highestTotalPrice : highestPrice) }}</template>
                </span>
              </div>
              <label class="pd-total-toggle">
                <input v-model="showTotalPrice" type="checkbox" class="pd-total-toggle__input" />
                <span class="pd-total-toggle__box" />
                <span class="pd-total-toggle__label">Gesamtpreis</span>
              </label>
            </div>

            <!-- Preisvergleich-Liste (moebel.de style) -->
            <div class="pd-offers">
              <div
                v-for="(offer, idx) in visibleOffers"
                :key="offer.id"
                :class="['pd-offer', idx === 0 && 'pd-offer--best']"
              >
                <!-- Shop Logo -->
                <div class="pd-offer__logo">
                  <img v-if="merchantFavicon(offer)" :src="merchantFavicon(offer)" :alt="offer.merchantName" @error="($event.target as HTMLImageElement).style.display='none'" />
                  <span v-else class="pd-offer__logo-fallback">{{ (offer.merchantName || '?')[0] }}</span>
                </div>
                <!-- Info -->
                <div class="pd-offer__info">
                  <div class="pd-offer__top">
                    <span class="pd-offer__price">{{ formatPrice(showTotalPrice ? offerTotalPrice(offer) : offer.price) }}</span>
                    <span v-if="offer.originalPrice && offer.originalPrice > offer.price" class="pd-offer__old-price">{{ formatPrice(offer.originalPrice) }}</span>
                    <MChip
                      v-if="offerDiscount(offer)"
                      color="secondary"
                      size="x-small"
                      variant="flat"
                    >
                      -{{ offerDiscount(offer) }} %
                    </MChip>
                    <span v-if="idx === 0 && allOffers.length > 1" class="pd-offer__best-label">Bester Gesamtpreis</span>
                  </div>
                  <div class="pd-offer__sub">
                    <template v-if="showTotalPrice">
                      inkl. Versand<template v-if="offerDiscount(offer)"> &amp; Rabatt</template>
                    </template>
                    <template v-else>
                      <template v-if="offerDeliveryCost(offer) === 0">versandkostenfrei</template>
                      <template v-else-if="offerDeliveryCost(offer) !== null">zzgl. {{ formatPrice(offerDeliveryCost(offer)!) }} Versand</template>
                    </template>
                    bei <strong>{{ offer.merchantName }}</strong>
                  </div>
                </div>
                <a
                  :href="offer.affiliateUrl"
                  target="_blank"
                  rel="noopener sponsored"
                  class="pd-offer__btn"
                  @click="onAffiliateClick"
                >
                  Zum Shop
                </a>
              </div>

              <!-- Mehr/Weniger anzeigen -->
              <button
                v-if="allOffers.length > 5"
                class="pd-offers__toggle"
                @click="showAllOffers = !showAllOffers"
              >
                <MIcon :icon="showAllOffers ? 'chevron-up' : 'chevron-down'" :size="18" class="mr-1" />
                {{ showAllOffers ? 'Weniger Angebote anzeigen' : `Alle ${allOffers.length} Angebote anzeigen` }}
              </button>
            </div>

            <!-- Zurück zur Kategorie -->
            <NuxtLink
              v-if="product.category"
              :to="buildCategoryPath(product.category)"
              class="pd-back-link mt-3"
            >
              <MIcon icon="chevron-left" :size="16" />
              Zurück zur Kategorie
            </NuxtLink>
          </div>
        </div>

        <!-- Produktdetails -->
        <section v-if="productAttributes.length" class="section" aria-labelledby="details-heading">
          <hr class="section-divider" />
          <h2 id="details-heading" class="section-title">Produktdetails</h2>
                <div class="m-row">
                  <div class="m-col m-col-12 m-col-md-6">
                    <h3 class="text-subtitle-2 mb-2">Maße</h3>
                    <ul class="pd-attr-list">
                      <li v-for="attr in dimensionAttrs" :key="attr.label">
                        <span class="text-caption text-muted">{{ attr.label }}:</span>
                        <span class="text-body-2">{{ attr.value }}</span>
                      </li>
                    </ul>
                  </div>
                  <div class="m-col m-col-12 m-col-md-6">
                    <h3 class="text-subtitle-2 mb-2">Farbe & Material</h3>
                    <ul class="pd-attr-list">
                      <li v-for="attr in materialAttrs" :key="attr.label">
                        <span class="text-caption text-muted">{{ attr.label }}:</span>
                        <template v-if="attr.label === 'Farbe'">
                          <span v-for="c in attr.value.split(/,\s*/)" :key="c" class="colour-dot ml-1" :style="{ backgroundColor: colourToHex(c.trim()) }" :title="c.trim()" />
                        </template>
                        <span v-else class="text-body-2">{{ attr.value }}</span>
                      </li>
                    </ul>
                  </div>
                </div>
                <div v-if="otherAttrs.length" class="mt-3">
                  <div class="m-row">
                    <div v-for="attr in otherAttrs" :key="attr.label" class="m-col m-col-6 m-col-sm-4 mb-2">
                      <span class="text-caption text-muted">{{ attr.label }}:</span>
                      <span class="text-body-2 ml-1">{{ attr.value }}</span>
                    </div>
                  </div>
                </div>
        </section>

        <!-- Beschreibung -->
        <section v-if="fullDescription" class="section" aria-labelledby="desc-heading">
          <hr class="section-divider" />
          <h2 id="desc-heading" class="section-title">Beschreibung</h2>
          <p class="text-body-2 text-muted product-description" :class="{ 'product-description--clamped': !descExpanded && isDescLong }">
            {{ descExpanded ? fullDescription : truncatedDescription }}
          </p>
          <MBtn
            v-if="isDescLong"
            variant="text"
            size="small"
            class="mt-1 px-0"
            @click="descExpanded = !descExpanded"
          >
            {{ descExpanded ? 'Weniger anzeigen' : 'Mehr anzeigen' }}
            <MIcon :icon="descExpanded ? 'chevron-up' : 'chevron-down'" :size="18" class="ml-1" />
          </MBtn>
        </section>

        <!-- Preisentwicklung -->
        <section v-if="product.ean" class="section" aria-labelledby="price-history-heading">
          <hr class="section-divider" />
          <PriceHistoryChart :ean="activeVariant?.ean || product.ean" />
        </section>

        <!-- Ähnliche Produkte (horizontales Karussell wie moebel.de) -->
        <section v-if="similarProducts.length > 0" class="section" aria-labelledby="similar-heading">
          <hr class="section-divider" />
          <h2 id="similar-heading" class="section-title">Ähnliche Produkte</h2>
          <div class="carousel-wrap">
            <button v-if="canScrollLeft" class="carousel-nav carousel-nav--left" aria-label="Links scrollen" @click="scrollCarousel(-1)">
              <MIcon icon="chevron-left" :size="24" />
            </button>
            <div ref="carouselRef" class="carousel-track" @scroll="updateCarouselNav">
              <div v-for="p in similarProducts.slice(0, 16)" :key="p.id" class="carousel-item">
                <ProductCard :product="p" />
              </div>
            </div>
            <button v-if="canScrollRight" class="carousel-nav carousel-nav--right" aria-label="Rechts scrollen" @click="scrollCarousel(1)">
              <MIcon icon="chevron-right" :size="24" />
            </button>
          </div>
        </section>

        <!-- Anderen gefällt auch (beliebt in Kategorie) -->
        <section v-if="popularProducts.length > 0" class="section" aria-labelledby="popular-heading">
          <hr class="section-divider" />
          <h2 id="popular-heading" class="section-title">Anderen gefällt auch</h2>
          <div class="carousel-wrap">
            <div class="carousel-track">
              <div v-for="p in popularProducts" :key="p.id" class="carousel-item">
                <ProductCard :product="p" />
              </div>
            </div>
          </div>
        </section>

        <!-- Deals in Kategorie -->
        <section v-if="dealProducts.length > 0" class="section" aria-labelledby="deals-heading">
          <hr class="section-divider" />
          <div class="d-flex align-center justify-space-between mb-3 ga-4">
            <h2 id="deals-heading" class="section-title mb-0">Deals — bis zu {{ maxDealDiscount }}% sparen!</h2>
            <NuxtLink v-if="categoryUrl" :to="categoryUrl + '?sort=discount'" class="text-decoration-none text-caption" style="color: var(--m-primary); white-space: nowrap">
              Alle Deals in {{ categoryName }} →
            </NuxtLink>
          </div>
          <div class="carousel-wrap">
            <div class="carousel-track">
              <div v-for="p in dealProducts" :key="p.id" class="carousel-item">
                <ProductCard :product="p" />
              </div>
            </div>
          </div>
        </section>

        <!-- Mehr von diesem Shop -->
        <section v-if="fromShopProducts.length > 0" class="section" aria-labelledby="shop-heading">
          <hr class="section-divider" />
          <h2 id="shop-heading" class="section-title">Mehr von {{ product.merchantName }}</h2>
          <div class="carousel-wrap">
            <div class="carousel-track">
              <div v-for="p in fromShopProducts" :key="p.id" class="carousel-item">
                <ProductCard :product="p" />
              </div>
            </div>
          </div>
        </section>

        <!-- Mehr entdecken (Kategorie-Chips) -->
        <section v-if="categoryBreadcrumb.length > 0" class="section" aria-labelledby="explore-heading">
          <hr class="section-divider" />
          <h2 id="explore-heading" class="section-title">Mehr entdecken auf möbix.de</h2>
          <div class="d-flex flex-wrap ga-2">
            <MChip
              v-for="bc in categoryBreadcrumb"
              :key="bc.slug"
              :to="bc.path"
              variant="outlined"
              size="small"
            >
              {{ bc.name }}
            </MChip>
          </div>
        </section>

        <!-- Zuletzt angesehen -->
        <ClientOnly>
          <section v-if="recentProducts.length > 0" class="section" aria-labelledby="recent-heading">
            <hr class="section-divider" />
            <h2 id="recent-heading" class="section-title">Zuletzt angesehen</h2>
            <div class="carousel-wrap">
              <div class="carousel-track">
                <div v-for="p in recentProducts" :key="p.id" class="carousel-item">
                  <ProductCard :product="p" />
                </div>
              </div>
            </div>
          </section>
        </ClientOnly>

        <!-- Lightbox Dialog -->
        <MDialog v-model="lightboxOpen" :max-width="900">
          <MCard class="pa-0 lightbox">
            <MBtn icon variant="text" size="small" class="lightbox__close" @click="lightboxOpen = false" aria-label="Schließen">
              <MIcon icon="close" />
            </MBtn>
            <div class="lightbox__body">
              <MBtn
                v-if="allImages.length > 1"
                icon
                variant="text"
                class="lightbox__nav lightbox__nav--prev"
                aria-label="Vorheriges Bild"
                @click="lightboxPrev"
              >
                <MIcon icon="chevron-left" :size="32" />
              </MBtn>
              <div class="lightbox__img-wrap">
                <MImg :src="allImages[lightboxIndex] || '/placeholder.svg'" :alt="`${product.name} Bild ${lightboxIndex + 1}`" contain class="lightbox__img" />
              </div>
              <MBtn
                v-if="allImages.length > 1"
                icon
                variant="text"
                class="lightbox__nav lightbox__nav--next"
                aria-label="Nächstes Bild"
                @click="lightboxNext"
              >
                <MIcon icon="chevron-right" :size="32" />
              </MBtn>
            </div>
            <div v-if="allImages.length > 1" class="d-flex justify-center flex-wrap m-gap-2 pa-3">
              <MAvatar
                v-for="(img, i) in allImages"
                :key="i"
                :size="56"
                rounded
                class="gallery__thumb"
                :class="{ 'gallery__thumb--active': i === lightboxIndex }"
                @click="lightboxIndex = i"
              >
                <MImg :src="img" :alt="`Bild ${i + 1}`" contain :width="56" :height="56" />
              </MAvatar>
            </div>
            <div v-if="allImages.length > 1" class="text-center text-caption text-muted pb-2">
              {{ lightboxIndex + 1 }} / {{ allImages.length }}
            </div>
          </MCard>
        </MDialog>
      </template>

      <!-- Nicht gefunden -->
      <div v-else class="text-center py-8">
        <MIcon icon="package-variant-closed" :size="64" color="var(--m-border)" class="mb-4" />
        <h2 class="text-h6 text-muted mb-2">Produkt nicht gefunden</h2>
        <MBtn color="primary" variant="flat" rounded to="/" class="mt-4">Zur Startseite</MBtn>
      </div>
    </div>

    <!-- Sticky Mobile CTA -->
    <Teleport to="body">
      <Transition name="slide-up">
        <div v-if="product && showStickyCta" class="sticky-cta">
          <div class="sticky-cta__inner">
            <div class="sticky-cta__info">
              <span class="sticky-cta__price">{{ formatPrice(activeVariant?.price || product.price) }}</span>
              <span class="sticky-cta__offers">{{ allOffers.length }} Angebote</span>
            </div>
            <a
              :href="allOffers[0]?.affiliateUrl || product.affiliateUrl"
              target="_blank"
              rel="noopener sponsored"
              class="sticky-cta__btn"
              @click="onAffiliateClick"
            >
              Zum Shop
            </a>
          </div>
        </div>
      </Transition>
    </Teleport>
  </div>
</template>

<script setup lang="ts">
import { getProductUrl } from "~/composables/useProductUrl";
import { trackProductView, trackAffiliateClick } from "~/composables/useProductTracking";
import { colourToHex, FALLBACK_COLOUR } from "~/utils/colour";
import { priceValidUntil } from "~/utils/seo";
import { parseDeliveryCost } from "~/utils/deliveryCost";
import { useRecentlyViewed } from "~/composables/useRecentlyViewed";

const api = useApi();

function resolveColour(variant: any): string {
  const meta = variant?.metadata || {};
  return meta.actualColour || variant?.variantColour || "";
}

function resolveColourHex(variant: any): string {
  return colourToHex(resolveColour(variant));
}

function capitalize(s: string): string {
  return s ? s.charAt(0).toUpperCase() + s.slice(1) : s;
}

const props = defineProps<{
  productData: any;
  loading?: boolean;
}>();

const product = computed(() => props.productData?.product || null);
const wishlistStore = useWishlistStore();

const recentlyViewed = useRecentlyViewed();
const recentProducts = computed(() => product.value ? recentlyViewed.getExcluding(product.value.id, 8) : []);

onMounted(() => {
  recentlyViewed.load();
});

watch(product, (p) => {
  if (!p || !import.meta.client) return;
  recentlyViewed.add({
    id: p.id,
    name: p.name,
    image: p.image,
    price: p.price,
    originalPrice: p.originalPrice,
    merchantName: p.merchantName,
    slug: p.slug,
    brand: p.brand,
    category: p.category,
  });
}, { immediate: true });

// --- Offers + Similar + Related (parallel SSR) ---
const [{ data: offersData }, { data: similarData }, { data: relatedData }] = await Promise.all([
  useAsyncData(
    () => `offers-${product.value?.id}`,
    () => {
      const ean = product.value?.ean;
      if (!ean) return Promise.resolve(null);
      return api<any>("/api/products", { query: { ean, perPage: 50 } });
    },
    { watch: [product] },
  ),
  useAsyncData(
    () => `similar-${product.value?.id}`,
    () => {
      if (!product.value?.id) return Promise.resolve(null);
      return api<any>(`/api/products/${product.value.id}/similar`);
    },
    { watch: [product] },
  ),
  useAsyncData(
    () => `related-${product.value?.id}`,
    () => {
      if (!product.value?.id) return Promise.resolve(null);
      return api<any>(`/api/products/${product.value.id}/related`);
    },
    { watch: [product] },
  ),
]);

const allOffers = computed(() => {
  const offers: any[] = [];
  if (product.value) offers.push(product.value);
  if (offersData.value?.products) {
    offers.push(...offersData.value.products.filter((p: any) => p.id !== product.value?.id));
  }
  return offers.sort((a: any, b: any) => a.price - b.price);
});

const cheapestPrice = computed(() => allOffers.value.length === 0 ? 0 : allOffers.value[0].price);
const highestPrice = computed(() => allOffers.value.length === 0 ? 0 : allOffers.value[allOffers.value.length - 1].price);
const cheapestTotalPrice = computed(() => allOffers.value.length === 0 ? 0 : Math.min(...allOffers.value.map(offerTotalPrice)));
const highestTotalPrice = computed(() => allOffers.value.length === 0 ? 0 : Math.max(...allOffers.value.map(offerTotalPrice)));

const showAllOffers = ref(false);
const showTotalPrice = ref(false);
const visibleOffers = computed(() => {
  if (showAllOffers.value || allOffers.value.length <= 5) return allOffers.value;
  return allOffers.value.slice(0, 5);
});

const similarProducts = computed(() => similarData.value?.products || []);

const popularProducts = computed(() => relatedData.value?.popular || []);
const dealProducts = computed(() => relatedData.value?.deals || []);
const fromShopProducts = computed(() => relatedData.value?.fromShop || []);

const maxDealDiscount = computed(() => {
  let max = 0;
  for (const p of dealProducts.value) {
    if (p.originalPrice && p.originalPrice > p.price) {
      max = Math.max(max, Math.round((1 - p.price / p.originalPrice) * 100));
    }
  }
  return max;
});

const categoryUrl = computed(() => {
  const cat = product.value?.category;
  if (!cat?.slug) return null;
  const parts = [cat.parent?.parent?.slug, cat.parent?.slug, cat.slug].filter(Boolean);
  return `/${parts.join("/")}`;
});

const categoryName = computed(() => product.value?.category?.name || "");

const categoryBreadcrumb = computed(() => {
  const cat = product.value?.category;
  if (!cat) return [];
  const crumbs: { name: string; slug: string; path: string }[] = [];
  if (cat.parent?.parent) {
    crumbs.push({ name: cat.parent.parent.name, slug: cat.parent.parent.slug, path: `/${cat.parent.parent.slug}` });
  }
  if (cat.parent) {
    const parentPath = cat.parent.parent ? `/${cat.parent.parent.slug}/${cat.parent.slug}` : `/${cat.parent.slug}`;
    crumbs.push({ name: cat.parent.name, slug: cat.parent.slug, path: parentPath });
  }
  crumbs.push({ name: cat.name, slug: cat.slug, path: categoryUrl.value || "/" });
  return crumbs;
});

// --- Variants ---
const activeVariantId = ref<string | null>(null);

const allVariants = computed(() => {
  const p = product.value;
  if (!p?.variants?.length) return [];
  return [
    { id: p.id, ean: p.ean, price: p.price, originalPrice: p.originalPrice, image: p.image, affiliateUrl: p.affiliateUrl, variantColour: p.variantColour, merchantName: p.merchantName, metadata: p.metadata },
    ...p.variants,
  ];
});

const displayVariants = computed(() => {
  const vars = allVariants.value;
  if (vars.length <= 1) return vars;
  const byImage = new Map<string, any>();
  for (const v of vars) {
    const img = v.image || "";
    const existing = byImage.get(img);
    if (!existing || v.price < existing.price) byImage.set(img, v);
  }
  return [...byImage.values()].map((v) => {
    const actual = resolveColour(v);
    const fabric = v.variantColour || "";
    const hasFabricCode = fabric && actual && fabric.toLowerCase() !== actual.toLowerCase();
    const label = actual ? capitalize(actual) + (hasFabricCode ? ` (${fabric})` : "") : fabric || "Standard";
    const sameImage = vars.filter((x: any) => x.image === v.image);
    const prices = sameImage.map((x: any) => x.price);
    const minPrice = Math.min(...prices);
    const maxPrice = Math.max(...prices);
    const priceText = minPrice === maxPrice ? `${formatPrice(minPrice)}` : `ab ${formatPrice(minPrice)}`;
    return { id: v.id, image: v.image, variant: v, label, tooltip: `${label} — ${priceText}` };
  });
});

const activeVariantLabel = computed(() => {
  const v = activeVariant.value;
  if (!v) return "Standard";
  const actual = resolveColour(v);
  const fabric = v.variantColour || "";
  const hasFabricCode = fabric && actual && fabric.toLowerCase() !== actual.toLowerCase();
  return actual ? capitalize(actual) + (hasFabricCode ? ` (${fabric})` : "") : fabric || "Standard";
});

const activeVariantColourLabel = computed(() => {
  const v = activeVariant.value || product.value;
  if (!v) return "";
  const meta = v.metadata || {};
  return meta.actualColour || v.variantColour || meta.colour || meta.fashionColour || "";
});

const dimensionsLabel = computed(() => {
  const p = product.value;
  if (!p) return "";
  const meta = p.metadata || {};
  if (meta.dimensions) return meta.dimensions;
  const fromName = p.name?.match(/[\d,.]+\s*(?:cm|mm|m)?\s*[x×]\s*[\d,.]+\s*(?:cm|mm|m)?(?:\s*[x×]\s*[\d,.]+\s*(?:cm|mm|m)?)?(?:\s*(?:cm|mm|m))?/i)?.[0]?.trim();
  return fromName || "";
});

const activeVariant = computed(() => {
  if (!allVariants.value.length) return null;
  return allVariants.value.find((v: any) => v.id === activeVariantId.value) || allVariants.value[0];
});

// --- Images ---
const activeImage = ref<string | null>(null);

function isPlaceholderImage(url: string): boolean {
  const lower = url.toLowerCase();
  return ["/noimage", "no-image", "no_image", "placeholder", "platzhalter", "productserve.com/noimage", "dummy", "coming-soon", "image-not-found"].some((p) => lower.includes(p));
}

const allImages = computed(() => {
  const p = product.value;
  if (!p) return [];
  const seen = new Set<string>();
  const imgs: string[] = [];
  const add = (url: string | null | undefined) => {
    if (!url || isPlaceholderImage(url) || seen.has(url)) return;
    seen.add(url);
    imgs.push(url);
  };
  if (p.images?.length) p.images.forEach(add);
  add(p.image);
  return imgs;
});

const activeImageIndex = computed(() => {
  const idx = allImages.value.indexOf(activeImage.value || "");
  return idx >= 0 ? idx : 0;
});

watch([product, activeVariant], () => {
  const variant = activeVariant.value;
  const p = product.value;
  activeImage.value = variant?.image || p?.image || p?.images?.[0] || null;
}, { immediate: true });

// --- Lightbox ---
const lightboxOpen = ref(false);
const lightboxIndex = ref(0);

function openLightbox(index: number) {
  lightboxIndex.value = index;
  lightboxOpen.value = true;
}

function lightboxPrev() {
  lightboxIndex.value = (lightboxIndex.value - 1 + allImages.value.length) % allImages.value.length;
}

function lightboxNext() {
  lightboxIndex.value = (lightboxIndex.value + 1) % allImages.value.length;
}

// --- Price ---
const displayOriginalPrice = computed(() => {
  const orig = activeVariant.value?.originalPrice || product.value?.originalPrice;
  const current = activeVariant.value?.price || product.value?.price;
  return orig && orig > current ? orig : null;
});

const discountPercent = computed(() => {
  if (!displayOriginalPrice.value) return 0;
  const current = activeVariant.value?.price || product.value?.price;
  return Math.round((1 - current / displayOriginalPrice.value) * 100);
});

// --- Description ---
const descExpanded = ref(false);
const DESC_LIMIT = 400;

const fullDescription = computed(() => {
  const desc = product.value?.description;
  if (!desc) return null;
  let text = desc.replace(/<br\s*\/?>/gi, "\n").replace(/<\/?(p|div|li|h\d)[^>]*>/gi, "\n").replace(/<[^>]*>/g, " ");
  text = decodeHtmlEntities(text);
  text = text.replace(/([a-z])([A-Z])/g, "$1 $2").replace(/(\d)(mm|cm|kg|dB|min)/g, "$1 $2").replace(/([.:])([A-Za-z])/g, "$1 $2").replace(/[ \t]+/g, " ").replace(/\n\s*\n+/g, "\n").trim();
  if (text.length < 20) return null;
  return text;
});

const isDescLong = computed(() => (fullDescription.value?.length || 0) > DESC_LIMIT);

const truncatedDescription = computed(() => {
  if (!fullDescription.value || !isDescLong.value) return fullDescription.value;
  return fullDescription.value.slice(0, DESC_LIMIT).replace(/\s\S*$/, "") + "\u2026";
});

const decodeHtmlEntities = (str: string) => str.replace(/&#(\d+)[;,]/g, (_, code) => String.fromCharCode(Number(code))).replace(/&amp;/g, "&").replace(/&lt[;,]/g, "<").replace(/&gt[;,]/g, ">").replace(/&quot[;,]/g, '"').replace(/&apos[;,]/g, "'").replace(/&nbsp[;,]/g, " ");

// --- Attributes ---
const productAttributes = computed(() => {
  const p = product.value;
  if (!p) return [];
  const meta = p.metadata || {};
  const attrs: { label: string; value: string }[] = [];
  const add = (label: string, value: string | null | undefined) => {
    if (value && value.trim()) attrs.push({ label, value: value.trim() });
  };
  add("Marke", p.brand);
  const displayColour = meta.actualColour || p.variantColour || meta.colour || meta.fashionColour;
  if (displayColour) {
    const unique = [...new Set(displayColour.split(/,\s*/).map((c: string) => c.trim()))].join(", ");
    add("Farbe", capitalize(unique));
  }
  if (p.variantColour && meta.actualColour && p.variantColour.toLowerCase() !== meta.actualColour.toLowerCase()) {
    add("Stoff/Bezug", p.variantColour);
  }
  add("Größe", p.variantSize || meta.fashionSize);
  const dimensionsFromName = p.name?.match(/[\d,.]+\s*(?:cm|mm|m)?\s*[x×]\s*[\d,.]+\s*(?:cm|mm|m)?(?:\s*[x×]\s*[\d,.]+\s*(?:cm|mm|m)?)?(?:\s*(?:cm|mm|m))?/i)?.[0]?.trim();
  add("Maße", meta.dimensions || dimensionsFromName);
  if (meta.specifications) {
    const knownDimLabels = new Set(["breite", "höhe", "tiefe", "länge", "durchmesser", "gewicht", "breite (b)", "höhe (h)", "tiefe (t)"]);
    const parts = String(meta.specifications).split(/\s*\|\s*/);
    const dimParts: string[] = [];
    const matParts: string[] = [];
    for (const part of parts) {
      const [rawLabel = "", ...rest] = part.split(":");
      const label = rawLabel.trim().toLowerCase();
      const val = rest.join(":").trim();
      if (!val || !label) continue;
      if (knownDimLabels.has(label)) dimParts.push(`${rawLabel.trim()}: ${val}`);
      else matParts.push(part.trim());
    }
    if (dimParts.length > 0 && !dimensionsFromName && !meta.dimensions) add("Abmessungen", dimParts.join(" | "));
    if (matParts.length > 0) add("Material / Ausführung", matParts.join(" | "));
  }
  add("Material", meta.material || meta.fashionMaterial);
  add("Oberfläche", meta.surface || meta.finish);
  add("Form", meta.shape || meta.form);
  add("Gewicht", meta.weight || meta.deliveryWeight ? `${meta.weight || meta.deliveryWeight} kg` : null);
  add("Stil", meta.style || meta.productType);
  add("Zustand", meta.condition === "new" ? "Neu" : meta.condition === "neu" ? "Neu" : meta.condition);
  add("EAN", p.ean);
  add("Lieferzeit", meta.deliveryTime);
  if (meta.deliveryCost !== undefined && meta.deliveryCost !== "") {
    const cost = parseFloat(meta.deliveryCost);
    if (!isNaN(cost)) add("Versand", cost === 0 ? "Kostenlos" : `${cost.toFixed(2)} €`);
  }
  return attrs;
});

const dimensionLabels = new Set(["Maße", "Abmessungen", "Größe", "Gewicht", "Form"]);
const materialLabels = new Set(["Farbe", "Stoff/Bezug", "Material", "Material / Ausführung", "Oberfläche"]);
const dimensionAttrs = computed(() => productAttributes.value.filter((a) => dimensionLabels.has(a.label)));
const materialAttrs = computed(() => productAttributes.value.filter((a) => materialLabels.has(a.label)));
const otherAttrs = computed(() => productAttributes.value.filter((a) => !dimensionLabels.has(a.label) && !materialLabels.has(a.label)));

// --- Breadcrumbs ---
const breadcrumbItems = computed(() => {
  const crumbs: Array<{ title: string; to?: string }> = [{ title: "Home", to: "/" }];
  const cat = product.value?.category;
  if (cat) {
    if (cat.parent) {
      const p = cat.parent;
      if (p.parent) {
        crumbs.push({ title: p.parent.name, to: `/${p.parent.slug}` });
        crumbs.push({ title: p.name, to: `/${p.parent.slug}/${p.slug}` });
      } else {
        crumbs.push({ title: p.name, to: `/${p.slug}` });
      }
    }
    crumbs.push({ title: cat.name, to: buildCategoryPath(cat) });
  }
  crumbs.push({ title: product.value?.name || "Produkt" });
  return crumbs;
});

function buildCategoryPath(cat: any): string {
  const parts: string[] = [];
  if (cat.parent?.parent?.slug) parts.push(cat.parent.parent.slug);
  if (cat.parent?.slug) parts.push(cat.parent.slug);
  parts.push(cat.slug);
  return `/${parts.join("/")}`;
}

// --- Merchant helpers ---
const TRACKING_DOMAINS = new Set(["awin1.com", "www.awin1.com", "a.nonstoppartner.net", "track.webgains.com", "click.linksynergy.com", "ad.doubleclick.net", "partners.webmasterplan.com", "tracking.belboon.com"]);

function merchantDomainFromName(name: string): string | null {
  if (!name) return null;
  if (/\.\w{2,}$/.test(name)) return name.toLowerCase();
  const cleaned = name.replace(/\s*(DE|AT|CH|DACH|DE\/AT|DE-AT|EU)\s*$/i, "").replace(/\s*-.*$/, "").trim();
  if (!cleaned) return null;
  const slug = cleaned.toLowerCase()
    .replace(/[äÄ]/g, "ae").replace(/[öÖ]/g, "oe").replace(/[üÜ]/g, "ue").replace(/ß/g, "ss")
    .replace(/\s+/g, "");
  return `${slug}.de`;
}

function merchantFavicon(offer: any): string {
  if (offer.shopUrl) {
    try {
      const hostname = new URL(offer.shopUrl).hostname;
      if (!TRACKING_DOMAINS.has(hostname)) return `https://www.google.com/s2/favicons?domain=${hostname}&sz=64`;
    } catch {}
  }
  const domain = merchantDomainFromName(offer.merchantName);
  if (domain) return `https://www.google.com/s2/favicons?domain=${domain}&sz=64`;
  if (offer.affiliateUrl) {
    try { return `https://www.google.com/s2/favicons?domain=${new URL(offer.affiliateUrl).hostname}&sz=64`; } catch {}
  }
  return "";
}

function offerDeliveryCost(offer: any): number | null {
  return parseDeliveryCost(offer.deliveryCost ?? offer.metadata?.deliveryCost) ?? null;
}

function offerTotalPrice(offer: any): number {
  const shipping = offerDeliveryCost(offer);
  return offer.price + (shipping ?? 0);
}

function offerDiscount(offer: any): number {
  if (!offer.originalPrice || offer.originalPrice <= offer.price) return 0;
  return Math.round((1 - offer.price / offer.originalPrice) * 100);
}

// --- Carousel ---
const carouselRef = ref<HTMLElement | null>(null);
const canScrollLeft = ref(false);
const canScrollRight = ref(true);

function updateCarouselNav() {
  const el = carouselRef.value;
  if (!el) return;
  canScrollLeft.value = el.scrollLeft > 10;
  canScrollRight.value = el.scrollLeft < el.scrollWidth - el.clientWidth - 10;
}

function scrollCarousel(dir: 1 | -1) {
  const el = carouselRef.value;
  if (!el) return;
  el.scrollBy({ left: dir * 300, behavior: "smooth" });
}

if (import.meta.client) {
  onMounted(() => nextTick(() => updateCarouselNav()));
}

// --- Gallery Nav ---
function prevImage() {
  const idx = allImages.value.indexOf(activeImage.value || "");
  const prev = (idx - 1 + allImages.value.length) % allImages.value.length;
  activeImage.value = allImages.value[prev] ?? null;
}

function nextImage() {
  const idx = allImages.value.indexOf(activeImage.value || "");
  const next = (idx + 1) % allImages.value.length;
  activeImage.value = allImages.value[next] ?? null;
}

// --- Actions ---
const isWishlisted = computed(() => product.value ? wishlistStore.isInWishlist(product.value.id) : false);

function onToggleWishlist() {
  const p = product.value;
  if (!p) return;
  wishlistStore.toggleItem({
    productId: p.id,
    name: p.name,
    price: p.price,
    image: p.image,
    affiliateUrl: p.affiliateUrl,
    merchantName: p.merchantName,
    ean: p.ean,
  });
}

function onAffiliateClick() {
  if (product.value?.id) trackAffiliateClick(product.value.id);
}

const canShare = computed(() => import.meta.client && typeof navigator !== "undefined" && !!navigator.share);

async function onShare() {
  if (!canShare.value || !product.value) return;
  try {
    await navigator.share({
      title: product.value.name,
      text: `${product.value.name} — ${formatPrice(product.value.price)} bei Moebix.de`,
      url: window.location.href,
    });
  } catch {}
}

// --- Sticky CTA ---
const showStickyCta = ref(false);

if (import.meta.client) {
  onMounted(() => {
    const onScroll = () => {
      showStickyCta.value = window.scrollY > 400;
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    onUnmounted(() => window.removeEventListener("scroll", onScroll));
  });
}

// --- View Tracking ---
if (import.meta.client) {
  onMounted(() => {
    if (product.value?.id) trackProductView(product.value.id);
  });
}

// --- Format ---
const formatPrice = (price: number) => new Intl.NumberFormat("de-DE", { style: "currency", currency: "EUR" }).format(price);

// --- Canonical URL ---
const canonicalUrl = computed(() => {
  if (!product.value) return "";
  const path = getProductUrl(product.value);
  return `https://www.moebix.de${path}`;
});

// --- SEO Meta ---
const seoTitle = computed(() => {
  const p = product.value;
  if (!p) return "Produkt — Moebix.de";
  const parts = [p.name];
  if (p.brand && !p.name.toLowerCase().includes(p.brand.toLowerCase())) parts.unshift(p.brand);
  parts.push(`ab ${formatPrice(cheapestPrice.value || p.price)}`);
  return `${parts.join(" ")} | Moebix.de`;
});

const seoDescription = computed(() => {
  const p = product.value;
  if (!p) return "";
  const parts: string[] = [];
  parts.push(`${p.name} günstig kaufen`);
  if (allOffers.value.length > 1) {
    parts.push(`${allOffers.value.length} Angebote ab ${formatPrice(cheapestPrice.value)}`);
  } else {
    parts.push(`für ${formatPrice(p.price)}`);
  }
  if (p.brand) parts.push(`von ${p.brand}`);
  parts.push("Preisvergleich, Preisentwicklung & Bewertungen bei Moebix.de");
  return parts.join(" — ");
});

useSeoMeta({
  title: seoTitle,
  description: seoDescription,
  ogTitle: computed(() => product.value?.name || "Produkt"),
  ogDescription: seoDescription,
  ogImage: computed(() => product.value?.image || undefined),
  ogType: "website",
  ogUrl: canonicalUrl,
  twitterCard: "summary_large_image",
  twitterTitle: computed(() => product.value?.name || "Produkt"),
  twitterDescription: seoDescription,
  twitterImage: computed(() => product.value?.image || undefined),
});

useHead({
  link: [{ rel: "canonical", href: canonicalUrl }],
  meta: [
    { property: "product:price:amount", content: computed(() => String(product.value?.price || "")) },
    { property: "product:price:currency", content: "EUR" },
    { property: "product:availability", content: computed(() => product.value?.inStock !== false ? "in stock" : "out of stock") },
  ],
  script: computed(() => {
    const p = product.value;
    if (!p) return [];
    const scripts: any[] = [];

    const validUntil = priceValidUntil();
    const offers = allOffers.value.map((o: any) => ({
      "@type": "Offer",
      url: o.affiliateUrl,
      priceCurrency: "EUR",
      price: o.price,
      priceValidUntil: validUntil,
      availability: o.inStock !== false ? "https://schema.org/InStock" : "https://schema.org/OutOfStock",
      itemCondition: "https://schema.org/NewCondition",
      seller: { "@type": "Organization", name: o.merchantName },
    }));

    scripts.push({
      type: "application/ld+json",
      innerHTML: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "Product",
        name: p.brand ? `${p.brand} ${p.name}` : p.name,
        image: allImages.value.length > 0 ? allImages.value : (p.image || undefined),
        description: fullDescription.value || `${p.name} günstig kaufen bei Moebix.de`,
        brand: p.brand ? { "@type": "Brand", name: p.brand } : undefined,
        gtin13: p.ean || undefined,
        sku: p.id,
        offers: offers.length > 1
          ? { "@type": "AggregateOffer", lowPrice: cheapestPrice.value, highPrice: highestPrice.value, priceCurrency: "EUR", offerCount: offers.length, priceValidUntil: validUntil, offers }
          : offers[0] || undefined,
      }),
    });

    const breadcrumbLd = breadcrumbItems.value.map((item, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: item.title,
      item: item.to ? `https://www.moebix.de${item.to}` : undefined,
    }));

    scripts.push({
      type: "application/ld+json",
      innerHTML: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: breadcrumbLd,
      }),
    });

    return scripts;
  }),
});
</script>

<style scoped>
/* --- Product Title --- */
.pd-title {
  font-family: "Play", sans-serif;
  font-size: 1.375rem;
  font-weight: 600;
  line-height: 1.3;
  color: var(--m-on-surface);
  margin-bottom: 8px;
}

/* --- Meta Info (moebel.de style: Alle Details | Farbe: Beige | Maße: ...) --- */
.pd-meta {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0;
  font-size: 0.8125rem;
  color: rgba(0, 0, 0, 0.55);
  margin-bottom: 16px;
}

.pd-meta__item {
  padding: 0 10px;
  border-right: 1px solid var(--m-border);
}

.pd-meta__item:first-child {
  padding-left: 0;
}

.pd-meta__item:last-child {
  border-right: none;
}

.pd-meta__link {
  color: var(--m-primary);
  cursor: pointer;
}

.pd-meta__item strong {
  color: var(--m-on-surface);
}

/* --- Variants (idealo-style cards) --- */
.pd-variants__label {
  font-size: 0.875rem;
  color: rgba(0, 0, 0, 0.6);
}

.pd-variant-card {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 10px;
  border: 1px solid var(--m-border);
  border-radius: var(--m-radius);
  cursor: pointer;
  transition: border-color var(--m-transition), box-shadow var(--m-transition);
  background: var(--m-surface);
}

.pd-variant-card:hover {
  border-color: rgba(0, 0, 0, 0.25);
}

.pd-variant-card--active {
  border-color: var(--m-primary);
  box-shadow: 0 0 0 1px var(--m-primary);
}

.pd-variant-card__img {
  border-radius: var(--m-radius);
  background: #fafafa;
  flex-shrink: 0;
}

.pd-variant-card__info {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.pd-variant-card__name {
  font-size: 0.75rem;
  color: var(--m-on-surface);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.pd-variant-card__price {
  font-size: 0.75rem;
  font-weight: 600;
  color: var(--m-primary);
}

/* --- Offers Header (moebel.de style) --- */
.pd-offers-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 6px;
}

.pd-offers-header__left {
  display: flex;
  align-items: baseline;
  gap: 8px;
}

.pd-offers-count {
  font-family: "Play", sans-serif;
  font-size: 1rem;
  font-weight: 700;
  color: var(--m-on-surface);
}

.pd-offers-range {
  font-size: 0.875rem;
  color: rgba(0, 0, 0, 0.5);
}

/* Gesamtpreis Toggle */
.pd-total-toggle {
  display: flex;
  align-items: center;
  gap: 6px;
  cursor: pointer;
  user-select: none;
}

.pd-total-toggle__input {
  width: 16px;
  height: 16px;
  accent-color: var(--m-primary);
  cursor: pointer;
  margin: 0;
}

.pd-total-toggle__label {
  font-size: 0.8125rem;
  color: rgba(0, 0, 0, 0.6);
}

.pd-total-toggle__box {
  display: none;
}

/* --- Offer List --- */
.pd-offers {
  border: 1px solid var(--m-border);
  border-radius: 4px;
  overflow: hidden;
  background: var(--m-surface);
}

.pd-offer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 16px;
  border-bottom: 1px solid var(--m-border-light);
  gap: 16px;
  transition: background var(--m-transition-fast);
}

.pd-offer:last-of-type {
  border-bottom: none;
}

.pd-offer:hover {
  background: rgba(0, 0, 0, 0.015);
}

.pd-offer--best {
  background: rgba(60, 143, 147, 0.04);
}

.pd-offer--best:hover {
  background: rgba(60, 143, 147, 0.07);
}

/* Logo */
.pd-offer__logo {
  width: 36px;
  height: 36px;
  flex-shrink: 0;
  border-radius: 50%;
  overflow: hidden;
  border: 1px solid rgba(0, 0, 0, 0.1);
  display: flex;
  align-items: center;
  justify-content: center;
  background: #fff;
  padding: 3px;
}

.pd-offer__logo img {
  width: 100%;
  height: 100%;
  object-fit: contain;
}

.pd-offer__logo-fallback {
  font-size: 0.875rem;
  font-weight: 700;
  color: var(--m-primary);
  text-transform: uppercase;
}

.pd-offer__info {
  flex: 1;
  min-width: 0;
}

.pd-offer__old-price {
  font-family: "Justtellmewhat", sans-serif;
  font-size: 0.9375rem;
  font-weight: 700;
  font-style: italic;
  color: var(--m-secondary);
  text-decoration: line-through;
  line-height: 1;
}

.pd-offer__top {
  display: flex;
  align-items: baseline;
  gap: 10px;
}

.pd-offer__price {
  font-family: "Justtellmewhat", sans-serif;
  font-size: 1.25rem;
  font-weight: 700;
  font-style: italic;
  color: var(--m-on-surface);
  line-height: 1;
}

.pd-offer--best .pd-offer__price {
  font-size: 1.25rem;
}

.pd-offer__best-label {
  font-size: 0.75rem;
  font-weight: 600;
  color: var(--m-primary);
}

.pd-offer__sub {
  font-size: 0.75rem;
  color: rgba(0, 0, 0, 0.5);
  margin-top: 3px;
}

.pd-offer__sub strong {
  color: rgba(0, 0, 0, 0.7);
}

/* Button (moebel.de salmon/rounded style) */
.pd-offer__btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 10px 28px;
  background: var(--m-primary);
  color: var(--m-on-primary);
  border-radius: 4px;
  font-size: 0.875rem;
  font-weight: 500;
  text-decoration: none;
  white-space: nowrap;
  flex-shrink: 0;
  transition: filter var(--m-transition-fast);
}

.pd-offer__btn:hover {
  background: var(--m-primary-dark);
}

/* --- Toggle Button --- */
.pd-offers__toggle {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  padding: 10px;
  border: none;
  background: none;
  cursor: pointer;
  font-size: 0.8125rem;
  color: var(--m-primary);
  font-weight: 500;
  border-top: 1px solid var(--m-border-light);
  transition: background var(--m-transition-fast);
}

.pd-offers__toggle:hover {
  background: rgba(0, 0, 0, 0.02);
}

/* --- Back Link --- */
.pd-back-link {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 0.8125rem;
  color: var(--m-primary);
  text-decoration: none;
}

.pd-back-link:hover {
  text-decoration: underline;
}

/* --- Sections --- */
.section { padding: 24px 0; }
.section-divider { border: none; border-top: 1px solid rgba(0, 0, 0, 0.08); margin: 0; }
.section-title { font-family: "Play", sans-serif; font-size: 1.125rem; font-weight: 600; color: var(--m-on-surface); margin-bottom: 16px; }

/* --- Description --- */
.product-description { line-height: 1.7; white-space: pre-line; }

/* --- Gallery --- */
.gallery__main-wrap { position: relative; aspect-ratio: 1; overflow: hidden; border-radius: var(--m-radius); background: var(--m-surface); }
@media (min-width: 960px) { .gallery__main-wrap { aspect-ratio: 4 / 3; } }
.gallery__main { cursor: zoom-in; transition: opacity 0.2s; height: 100%; }
.gallery__main:hover { opacity: 0.95; }
:deep(.gallery__main img) { object-fit: contain; width: 100%; height: 100%; }

.gallery__actions {
  position: absolute;
  top: 8px;
  right: 8px;
  display: flex;
  gap: 6px;
  z-index: 2;
}

.gallery__action-btn {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  border: none;
  background: rgba(255, 255, 255, 0.9);
  backdrop-filter: blur(4px);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.1);
  transition: background 0.15s;
}

.gallery__action-btn:hover {
  background: #fff;
}

.gallery__nav {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  width: 32px;
  height: 32px;
  border-radius: 50%;
  border: none;
  background: rgba(255, 255, 255, 0.85);
  backdrop-filter: blur(4px);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.12);
  z-index: 2;
  transition: background 0.15s;
}

.gallery__nav:hover { background: #fff; }
.gallery__nav--prev { left: 8px; }
.gallery__nav--next { right: 8px; }

.gallery__thumb { border: 2px solid transparent; cursor: pointer; opacity: 0.6; transition: all 0.15s ease; }
.gallery__thumb:hover { opacity: 0.9; }
.gallery__thumb--active { border-color: var(--m-primary); opacity: 1; }

/* --- Lightbox --- */
.lightbox { position: relative; }
.lightbox__close { position: absolute; top: 8px; right: 8px; z-index: 1; }
.lightbox__body { position: relative; display: flex; align-items: center; justify-content: center; }
.lightbox__img-wrap { width: 100%; height: 60vh; display: flex; align-items: center; justify-content: center; background: var(--m-surface); }
.lightbox__img { max-height: 100%; max-width: 100%; }
:deep(.lightbox__img img) { object-fit: contain; max-height: 60vh; }
.lightbox__nav { position: absolute; top: 50%; transform: translateY(-50%); z-index: 1; background: rgba(255,255,255,0.8) !important; }
.lightbox__nav--prev { left: 8px; }
.lightbox__nav--next { right: 8px; }

/* --- Colour Dots --- */
.colour-dot { display: inline-block; width: 22px; height: 22px; border-radius: 50%; border: 1px solid rgba(0, 0, 0, 0.15); flex-shrink: 0; }
.pd-attr-list { list-style: none; padding: 0; margin: 0; }
.pd-attr-list li { display: flex; align-items: center; gap: 6px; padding: 4px 0; border-bottom: 1px solid var(--m-border-light); }
.pd-attr-list li:last-child { border-bottom: none; }

/* --- Carousel (moebel.de style horizontal scroll) --- */
.carousel-wrap {
  position: relative;
}

.carousel-track {
  display: flex;
  gap: 12px;
  overflow-x: auto;
  scroll-snap-type: x mandatory;
  scrollbar-width: none;
  -ms-overflow-style: none;
  padding-bottom: 4px;
}

.carousel-track::-webkit-scrollbar {
  display: none;
}

.carousel-item {
  flex: 0 0 180px;
  scroll-snap-align: start;
}

@media (min-width: 600px) {
  .carousel-item { flex: 0 0 200px; }
}

.carousel-nav {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  width: 36px;
  height: 36px;
  border-radius: 50%;
  border: 1px solid var(--m-border);
  background: rgba(255, 255, 255, 0.95);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  z-index: 2;
  transition: box-shadow 0.15s;
}

.carousel-nav:hover {
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.15);
}

.carousel-nav--left { left: -12px; }
.carousel-nav--right { right: -12px; }

/* --- Sticky Mobile CTA --- */
.sticky-cta {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  z-index: 100;
  background: rgba(255, 255, 255, 0.97);
  backdrop-filter: blur(12px);
  border-top: 1px solid rgba(0, 0, 0, 0.08);
  box-shadow: 0 -2px 12px rgba(0, 0, 0, 0.08);
  padding: 10px 16px;
  padding-bottom: max(10px, env(safe-area-inset-bottom));
}

.sticky-cta__inner {
  display: flex;
  align-items: center;
  justify-content: space-between;
  max-width: 1200px;
  margin: 0 auto;
  gap: 12px;
}

.sticky-cta__info { display: flex; align-items: center; gap: 8px; }

.sticky-cta__price {
  font-family: "Justtellmewhat", sans-serif;
  font-size: 1.5rem;
  font-weight: 700;
  font-style: italic;
  color: var(--m-primary);
  line-height: 1;
}

.sticky-cta__offers {
  font-size: 0.75rem;
  color: rgba(0, 0, 0, 0.5);
}

.sticky-cta__btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 10px 24px;
  background: var(--m-primary);
  color: var(--m-on-primary);
  border-radius: 4px;
  font-size: 0.875rem;
  font-weight: 500;
  text-decoration: none;
  white-space: nowrap;
  transition: filter var(--m-transition-fast);
}

.sticky-cta__btn:hover {
  background: var(--m-primary-dark);
}

@media (min-width: 960px) {
  .sticky-cta { display: none; }
}

/* --- Slide-up transition --- */
.slide-up-enter-active, .slide-up-leave-active { transition: transform 0.25s ease, opacity 0.25s ease; }
.slide-up-enter-from, .slide-up-leave-to { transform: translateY(100%); opacity: 0; }

.text-decoration-none { text-decoration: none; }
</style>
